import { useQuery } from "@tanstack/react-query"

/**
 * Planer im Schatten (Baustein 14, 27.09.2026): das führende Modell (Sol oder
 * Sonnet) plant für das Büro, das andere plant dieselbe Mail im Hintergrund
 * nur lesend mit. Hier steht, wie oft beide beim Kunden, beim Vorgang, bei
 * der Handlungsart und beim Termin übereinstimmen — und was es kostet.
 */

type Quote = { gleich: number; n: number }
type Kern = {
  kunde: string; kunde_neu: boolean; vorgang: string
  handlungsart: string | null; termin: boolean; aktionen: string[]; rueckfragen: number
}
export type SchattenAuswertung = {
  an: boolean
  modus: string
  schatten: string
  anteil: number
  je_richtung: Record<string, number>
  deckel_usd: number
  heute_usd: number
  tage: number
  faelle: number
  fehler: number
  quoten: Record<"kunde" | "vorgang" | "handlungsart" | "termin", Quote>
  kosten: { schatten_summe_usd: number; sol_je_mail_usd: number | null; sonnet_je_mail_usd: number | null }
  dauer_s_mittel: number | null
  abweichungen: { email_id: number; betreff: string; zeit: string | null; fuehrend: string; abweichend: string[]; sonnet: Kern; sol: Kern }[]
}

const PUNKTE: [keyof SchattenAuswertung["quoten"], string][] = [
  ["kunde", "Kunde"], ["vorgang", "Vorgang"], ["handlungsart", "Handlungsart"], ["termin", "Termin ja/nein"],
]

const NAME: Record<string, string> = { sol: "Sol", sonnet: "Sonnet" }

function quote(q?: Quote) {
  if (!q || q.n === 0) return "—"
  return `${Math.round((q.gleich / q.n) * 100)} %`
}

function cent(usd: number | null | undefined) {
  if (usd == null) return "—"
  return `${(usd * 100).toFixed(1).replace(".", ",")} ¢`
}

function kernText(k: Kern) {
  const teile = [
    k.kunde_neu ? "Kunde neu" : `Kunde ${k.kunde}`,
    k.vorgang ? `Vorgang ${k.vorgang}` : "",
    k.handlungsart || "",
    k.termin ? "mit Termin" : "ohne Termin",
  ]
  return teile.filter(Boolean).join(" · ")
}

export function PlanerSolSchattenBlock({ tage, tenant }: { tage: number; tenant: string }) {
  const q = useQuery<SchattenAuswertung>({
    queryKey: ["planer-sol-schatten", tage, tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ tage: String(Math.min(tage, 90)) })
      if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/rueckkopplung/planer-sol?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    staleTime: 1000 * 60,
  })
  const d = q.data
  // Unerwartete Antwort (älterer Server ohne Endpunkt) = Block weglassen, nicht die Seite sprengen.
  if (q.isLoading || q.error || !d || !d.quoten || !d.kosten) return null
  if (!d.an && d.faelle === 0) return null

  return (
    <section aria-label="Planer im Schatten" className="mb-5">
      <h2 className="text-[14px] font-semibold mb-2">
        Planer im Schatten ({d.faelle} Mails, letzte {d.tage} Tage)
      </h2>
      <p className="text-[12.5px] text-muted-foreground mb-2 max-w-[72ch]">
        {d.an
          ? `Läuft: ${NAME[d.modus] ?? d.modus} plant für das Büro, ${NAME[d.schatten] ?? d.schatten} plant dieselbe Mail im Hintergrund nur lesend mit`
          + (d.anteil < 1 ? ` (Stichprobe ${Math.round(d.anteil * 100)} % der Mails)` : "") + ". "
          : "Aus. Bisher gesammelt. "}
        Heute {cent(d.heute_usd)} von {cent(d.deckel_usd)} Tagesdeckel.
      </p>
      <div className="grid md:grid-cols-3 gap-3 mb-2">
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Gleich wie Sonnet</div>
          <ul>
            {PUNKTE.map(([k, label]) => (
              <li key={k}><strong>{label}</strong>: {quote(d.quoten[k])}
                {d.quoten[k]?.n ? <span className="text-muted-foreground"> ({d.quoten[k].gleich} von {d.quoten[k].n})</span> : null}
              </li>
            ))}
          </ul>
          {d.fehler > 0 && <div className="mt-1 text-muted-foreground">{d.fehler} ohne Sol-Plan (Fehler)</div>}
        </div>
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Kosten je Mail</div>
          <div><strong>Sol</strong>: {cent(d.kosten.sol_je_mail_usd)}</div>
          <div><strong>Sonnet</strong> (Ermittlung + Plan): {cent(d.kosten.sonnet_je_mail_usd)}</div>
          <div className="mt-1 text-muted-foreground">Schatten gesamt {cent(d.kosten.schatten_summe_usd)}</div>
        </div>
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Dauer im Schatten</div>
          <div>{d.dauer_s_mittel != null ? `${String(d.dauer_s_mittel).replace(".", ",")} s im Mittel` : "—"}</div>
        </div>
      </div>
      {(d.abweichungen || []).length > 0 && (
        <details className="text-[12.5px]">
          <summary className="cursor-pointer">Abweichungen ({d.abweichungen.length} zuletzt)</summary>
          <ul className="mt-1 ml-4 list-disc">
            {d.abweichungen.map((a) => (
              <li key={a.email_id} className="mb-1">
                <strong>{a.betreff || "(ohne Betreff)"}</strong> — anders: {a.abweichend.join(", ")}
                <div className="text-muted-foreground">Sonnet: {kernText(a.sonnet)}</div>
                <div className="text-muted-foreground">Sol: {kernText(a.sol)}</div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
